import { Component, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { RequestRoot } from 'types';
import { ValidationService } from '../validation/validation.service';
import { AppContext, WizardContext } from '../control/context';
import { FeatureArray } from '../model/features';
import { FeaturesImpl } from '../model/features-impl';
import { VirtualKeyboardService } from '../virtual-keyboard/virtual-keyboard.service';
import { TableData, TableItem } from '../table/table';
import { TableEditorDialogBoxComponent, ColumnData } from '../table-editor-dialog-box/table-editor-dialog-box.component';
import { AnimationsConfig } from '../../service/animations-config';
import { ResizedEvent } from '../directive/element-resize.directive';
import { MsgBoxComponent } from '../msgbox/msgbox.component';

@Component({
  selector: 'i-table-editor',
  templateUrl: './table-editor.component.html',
  styleUrls: ['./table-editor.component.scss'],
})
export class TableEditorComponent {
  @Input()
  inpModel: FeatureArray<any>;
  @Output()
  inpModelChange = new EventEmitter<FeatureArray<any> | null>();
  @Input()
  rootModel: RequestRoot;
  @Input()
  rootPath: string;
  @Input()
  dialogTitle?: string;

  isMobile: boolean = false;
  displayedColumns: ColumnData[] = [];
  columns: string[] = [];
  dataSource: TableData<any> = new TableData<any>(0, []);

  private _lastWidth: number | null = null;

  constructor(
    public dialog: MatDialog,
    public appContext: AppContext,
    private overlay: OverlayContainer,
    private virtualKeyboardService: VirtualKeyboardService,
    private wizardContext: WizardContext,
    private validationService: ValidationService,
    private animationsConfig: AnimationsConfig,
    private cdr: ChangeDetectorRef,
  ) {}

  getI18nName(key: string): string {
    return this.inpModel.resourceKeyPrefix + key;
  }

  getI18nMiss(key: string): string {
    return 'MISS.' + this.inpModel.resourceKeyPrefix + key;
  }

  addRowData(element?: any) {
    if (!element) {
      element = new this.inpModel.template.constructor();
    }
    const index = this.inpModel.push(element) - 1;
    this.openDialog(index).subscribe((res) => {
      if (res?.dialogResult !== 'ok') {
        this.inpModel.splice(index);
      }
      const compareFunction = FeaturesImpl.getArrayCompareFunction(this.inpModel.template);
      if (compareFunction) {
        this.inpModel.sort(compareFunction);
      }
      this.dataSource.items = this.inpModel.map((x) => new TableItem(x));

      let nextElement = null;
      if (res?.dialogResult === 'ok') {
        const onEditClosed = FeaturesImpl.getArrayEditClosedCallback(this.inpModel.template);
        if (onEditClosed) {
          nextElement = onEditClosed(res.element, this.dataSource.items.map(x => x.item));
        }
      }

      if (nextElement) {
        this.addRowData(nextElement);
      } else {
        this.validationService.validate();
      }
      this.appContext.storeDraft(this.rootModel);
      this.cdr.detectChanges();
    });
  }

  updateRowData(index: number) {
    const elementCopy = new this.inpModel.template.constructor();
    FeaturesImpl.readTree(JSON.parse(JSON.stringify(this.inpModel[index])), elementCopy);
    this.openDialog(index).subscribe((res) => {
      if (res?.dialogResult !== 'ok') {
        this.inpModel[index] = elementCopy;
      }
      const compareFunction = FeaturesImpl.getArrayCompareFunction(this.inpModel.template);
      if (compareFunction) {
        this.inpModel.sort(compareFunction);
      }
      this.dataSource.items = this.inpModel.map((x) => new TableItem(x));
      this.validationService.validate();
      this.appContext.storeDraft(this.rootModel);
      this.cdr.detectChanges();
    });
  }

  deleteRowData(index: number) {
    this.dialog.open(MsgBoxComponent, {
      data: {
        title: 'Подтверждение',
        content: 'Вы уверены, что хотите удалить выбранную запись?',
        buttons: ['Oк', 'Отмена']
      },
      backdropClass: this.animationsConfig.backdropClass() || undefined,
      panelClass: 'table-editor-dialog',
      disableClose: true,
    }).afterClosed().subscribe((result) => {
      if (result === 1) {
        this.inpModel.splice(index, 1);
        this.dataSource.items = this.inpModel.map((x) => new TableItem(x));
        this.validationService.validate();
        this.appContext.storeDraft(this.rootModel);
        this.cdr.detectChanges();
      }
    });
  }

  openDialog(index: number): Observable<any> {
    this.addOverlayClass();
    const rootPath = this.rootPath + '.' + index;
    const cols: ColumnData[] = [...this.displayedColumns];
    cols.forEach((c) => (c.fullPath = rootPath + '.' + c.path));
    const dialogRef = this.dialog.open(TableEditorDialogBoxComponent, {
      data: {
        rootModel: this.rootModel,
        columns: cols,
        element: this.inpModel[index],
        elementTemplate: this.inpModel.template,
        wizardContext: this.wizardContext,
        rootPath: rootPath,
        dialogTitle: this.dialogTitle,
      },
      backdropClass: this.animationsConfig.backdropClass() || undefined,
      panelClass: 'table-editor-dialog',
      disableClose: true,
    });
    this.virtualKeyboardService.center(dialogRef);
    this.appContext.closeOnNavigation(dialogRef.afterClosed(), () => dialogRef.close());
    return dialogRef.afterClosed().pipe(
      tap(() => {
        this.removeOverlayClass();
      }),
    );
  }

  getColumnFlex(propertyPath: string): string {
    return FeaturesImpl.getArrayViewFlexStyle(this.inpModel.template, propertyPath);
  }

  formatProp(row: any, propName: string) {
    const pth = this.rootPath + '.' + this.dataSource.items.indexOf(row) + '.' + propName;
    return FeaturesImpl.makeString(this.rootModel, pth, this.appContext, this.getI18nMiss(propName));
  }

  getIsCompact(row: any, propName: string): boolean {
    const info = FeaturesImpl.arrayColumnInfos.find(it => it.rootPrototype === Object.getPrototypeOf(row.item) && it.prop === propName)
    return info?.compact ?? false;
  }

  formatFirstLine(row: any) {
    return FeaturesImpl.formatMobileArrayFirstLine(row, this.appContext);
  }

  formatSecondLine(row: any) {
    return FeaturesImpl.formatMobileArraySecondLine(row, this.appContext);
  }

  onTableResized($event: ResizedEvent) {
    if (this._lastWidth !== $event.width) {
      this.renderTable($event.width);
    }
  }

  private renderTable(tableWidth: number = 0) {
    setTimeout(() => {
      this.isMobile = tableWidth <= 640;
      if (this.displayedColumns.length === 0) {
        this.initDisplayedColumns();
      }
      this._lastWidth = tableWidth;
      this.columns = this.displayedColumns
        .filter((x) => FeaturesImpl.getArrayViewTablePropVisible(this.inpModel.template, x.path))
        .map((x) => x.path);
      this.columns.push('.action');
      this.dataSource.items = this.inpModel.map((x) => new TableItem(x));
    }, 100);
  }

  private initDisplayedColumns(): void {
    const arrayElementProperties = FeaturesImpl.tree.get(Object.getPrototypeOf(this.inpModel.template)) || [];
    this.displayedColumns = arrayElementProperties.map((x) => ({
      title: this.appContext.getI18NString(this.getI18nName(x.prop)),
      i18n_name: this.getI18nName(x.prop),
      i18n_miss: this.getI18nMiss(x.prop),
      path: x.prop,
      fullPath: '',
      setters: FeaturesImpl.getArrayEditSetters(this.inpModel.template, x.prop),
    }));
  }

  private addOverlayClass(): void {
    let e = this.overlay.getContainerElement();
    e.classList.add('table-editor-overlay');
  }

  private removeOverlayClass(): void {
    let e = this.overlay.getContainerElement();
    e.classList.remove('table-editor-overlay');
  }
}
